/**
 * IDF Document Store
 *
 * Zustand store for managing the parsed IDF document state.
 * Provides actions for parsing, editing, and serializing IDF content.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  type IDFDocument,
  type IDFField,
  type IDFObject,
  parseIDF,
  type ParseError,
  serializeIDF,
} from '../parser';
import { useIDDStore } from './iddStore';

interface IDFState {
  /** The parsed IDF document */
  document: IDFDocument | null;

  /** Raw IDF text (source of truth before parsing) */
  rawContent: string;

  /** Parse errors and warnings */
  errors: ParseError[];

  /** Whether the document has been successfully parsed */
  isParsed: boolean;

  /** Whether there are unsaved changes */
  isDirty: boolean;

  /** Currently selected object ID */
  selectedObjectId: string | null;

  /** Search/filter query */
  filterQuery: string;

  // Actions

  /** Parse raw IDF content into structured document */
  parseContent: (content: string) => void;

  /** Update raw content without parsing (for editor typing) */
  setRawContent: (content: string) => void;

  /** Serialize document back to IDF text */
  serialize: () => string;

  /** Update a specific object in the document */
  updateObject: (objectId: string, updates: Partial<IDFObject>) => void;

  /** Update a specific field in an object */
  updateField: (objectId: string, fieldIndex: number, field: Partial<IDFField>) => void;

  /** Update a specific field across multiple objects (for bulk editing) */
  updateFieldForMultipleObjects: (
    objectIds: string[],
    fieldIndex: number,
    field: Partial<IDFField>
  ) => void;

  /** Add a new object to the document */
  addObject: (obj: Omit<IDFObject, 'id'>) => string;

  /** Remove an object from the document */
  removeObject: (objectId: string) => void;

  /** Duplicate an existing object */
  duplicateObject: (objectId: string) => string | null;

  /** Move an object to a new position */
  moveObject: (objectId: string, newIndex: number) => void;

  /** Select an object */
  selectObject: (objectId: string | null) => void;

  /** Set filter query */
  setFilterQuery: (query: string) => void;

  /** Get objects filtered by class name */
  getObjectsByClass: (className: string) => IDFObject[];

  /** Get a specific object by ID */
  getObjectById: (objectId: string) => IDFObject | undefined;

  /** Mark document as clean (saved) */
  markClean: () => void;

  /** Clear the document */
  clear: () => void;
}

/**
 * Generate a unique ID for IDF objects.
 */
function generateId(): string {
  return `obj_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Recalculate source locations for all objects in the document.
 * This must be called after serialization to keep line numbers in sync
 * with the actual content in the editor.
 */
function recalculateSourceLocations(document: IDFDocument): void {
  let currentLine = 1;

  // Account for header comments
  for (const _comment of document.headerComments) {
    currentLine++;
  }

  // Blank line after header comments if any
  if (document.headerComments.length > 0) {
    currentLine++;
  }

  // Calculate source location for each object
  for (let i = 0; i < document.objects.length; i++) {
    const obj = document.objects[i];
    if (!obj) continue;

    // Leading comments
    const leadingCommentLines = obj.leadingComments.length;
    currentLine += leadingCommentLines;

    const startLine = currentLine;

    // Class name line
    currentLine++;

    // Field lines (each field is on its own line in standard format)
    const fieldLines = obj.fields.length;
    currentLine += fieldLines;

    // Handle objects with no fields - class name ends with semicolon on same line
    // In this case we already counted the class name line, no extra lines needed

    const endLine = currentLine - 1;

    // Update the object's source location
    obj.sourceLocation = {
      startLine,
      endLine: Math.max(startLine, endLine),
    };

    // Blank line between objects (except after last one)
    if (i < document.objects.length - 1) {
      currentLine++;
    }
  }
}

export const useIDFStore = create<IDFState>()(
  devtools(
    (set, get) => {
      /**
       * Helper to sync rawContent with the current document state.
       * Called after any mutation to the document structure to keep
       * the code view in sync with visual editor changes.
       */
      const syncRawContent = () => {
        const { document } = get();
        if (document) {
          const serialized = serializeIDF(document);
          // Recalculate source locations so highlights stay in sync with the new content
          recalculateSourceLocations(document);
          set({ rawContent: serialized });
        }
      };

      return {
        document: null,
        rawContent: '',
        errors: [],
        isParsed: false,
        isDirty: false,
        selectedObjectId: null,
        filterQuery: '',

        parseContent: (content: string) => {
        const result = parseIDF(content);
        set({
          document: result.document,
          rawContent: content,
          errors: result.errors,
          isParsed: result.success,
          isDirty: false,
        });
      },

      setRawContent: (content: string) => {
        set({
          rawContent: content,
          isDirty: true,
        });
      },

      serialize: () => {
        const { document } = get();
        if (!document) return '';
        return serializeIDF(document);
      },

      updateObject: (objectId: string, updates: Partial<IDFObject>) => {
        set((state) => {
          if (!state.document) return state;

          const objects = state.document.objects.map((obj) =>
            obj.id === objectId ? { ...obj, ...updates } : obj
          );

          return {
            document: { ...state.document, objects },
            isDirty: true,
          };
        });

        // Sync rawContent so code view reflects the changes
        syncRawContent();
      },

      updateField: (objectId: string, fieldIndex: number, fieldUpdates: Partial<IDFField>) => {
        set((state) => {
          if (!state.document) return state;

          // Get IDD schema for field name lookup
          const iddStore = useIDDStore.getState();

          const objects = state.document.objects.map((obj) => {
            if (obj.id !== objectId) return obj;

            // Create a copy of fields array
            const fields = [...obj.fields];

            // Expand array with empty fields if needed to accommodate fieldIndex
            while (fields.length <= fieldIndex) {
              fields.push({ value: '' });
            }

            // Add inline comments from IDD schema to all fields up to fieldIndex
            // that don't already have comments
            for (let idx = 0; idx <= fieldIndex; idx++) {
              const field = fields[idx];
              if (field && !field.inlineComment) {
                const fieldInfo = iddStore.getField(obj.className, idx);
                const inlineComment = fieldInfo?.name || fieldInfo?.id;
                if (inlineComment) {
                  fields[idx] = { ...field, inlineComment, value: field.value };
                }
              }
            }

            // Update the field at the specified index, ensuring value is always defined
            const existingField = fields[fieldIndex];
            fields[fieldIndex] = {
              ...existingField,
              ...fieldUpdates,
              value: fieldUpdates.value ?? existingField?.value ?? '',
            };

            return { ...obj, fields };
          });

          return {
            document: { ...state.document, objects },
            isDirty: true,
          };
        });

        // Sync rawContent so code view reflects the changes
        syncRawContent();
      },

      updateFieldForMultipleObjects: (
        objectIds: string[],
        fieldIndex: number,
        fieldUpdates: Partial<IDFField>
      ) => {
        set((state) => {
          if (!state.document || objectIds.length === 0) return state;

          // Get IDD schema for field name lookup
          const iddStore = useIDDStore.getState();

          const objectIdSet = new Set(objectIds);
          const objects = state.document.objects.map((obj) => {
            if (!objectIdSet.has(obj.id)) return obj;

            // Create a copy of fields array
            const fields = [...obj.fields];

            // Expand array with empty fields if needed to accommodate fieldIndex
            while (fields.length <= fieldIndex) {
              fields.push({ value: '' });
            }

            // Add inline comments from IDD schema to all fields up to fieldIndex
            // that don't already have comments
            for (let idx = 0; idx <= fieldIndex; idx++) {
              const field = fields[idx];
              if (field && !field.inlineComment) {
                const fieldInfo = iddStore.getField(obj.className, idx);
                const inlineComment = fieldInfo?.name || fieldInfo?.id;
                if (inlineComment) {
                  fields[idx] = { ...field, inlineComment, value: field.value };
                }
              }
            }

            // Update the field at the specified index, ensuring value is always defined
            const existingField = fields[fieldIndex];
            fields[fieldIndex] = {
              ...existingField,
              ...fieldUpdates,
              value: fieldUpdates.value ?? existingField?.value ?? '',
            };

            return { ...obj, fields };
          });

          return {
            document: { ...state.document, objects },
            isDirty: true,
          };
        });

        // Sync rawContent so code view reflects the changes
        syncRawContent();
      },

      addObject: (objData: Omit<IDFObject, 'id'>) => {
        const id = generateId();
        const newObj: IDFObject = { ...objData, id };

        set((state) => {
          if (!state.document) {
            // Create new document with this object
            return {
              document: {
                version: '',
                objects: [newObj],
                headerComments: [],
                trailingComments: [],
              },
              isDirty: true,
              isParsed: true,
            };
          }

          return {
            document: {
              ...state.document,
              objects: [...state.document.objects, newObj],
            },
            isDirty: true,
          };
        });

        // Sync rawContent so code view reflects the new object
        syncRawContent();

        return id;
      },

      removeObject: (objectId: string) => {
        set((state) => {
          if (!state.document) return state;

          const objects = state.document.objects.filter((obj) => obj.id !== objectId);

          return {
            document: { ...state.document, objects },
            isDirty: true,
            selectedObjectId: state.selectedObjectId === objectId ? null : state.selectedObjectId,
          };
        });

        // Sync rawContent so code view reflects the removal
        syncRawContent();
      },

      duplicateObject: (objectId: string) => {
        const { document } = get();
        if (!document) return null;

        const original = document.objects.find((obj) => obj.id === objectId);
        if (!original) return null;

        const id = generateId();
        const duplicate: IDFObject = {
          ...original,
          id,
          fields: original.fields.map((f) => ({ ...f })),
          leadingComments: [...original.leadingComments],
        };

        // Update name field if present (usually first field)
        const firstField = duplicate.fields[0];
        if (firstField && firstField.inlineComment?.toLowerCase().includes('name')) {
          duplicate.fields[0] = {
            ...firstField,
            value: `${firstField.value}_copy`,
          };
        }

        set((state) => {
          if (!state.document) return state;

          const originalIndex = state.document.objects.findIndex((obj) => obj.id === objectId);
          const objects = [...state.document.objects];
          objects.splice(originalIndex + 1, 0, duplicate);

          return {
            document: { ...state.document, objects },
            isDirty: true,
          };
        });

        // Sync rawContent so code view reflects the duplicate
        syncRawContent();

        return id;
      },

      moveObject: (objectId: string, newIndex: number) => {
        set((state) => {
          if (!state.document) return state;

          const objects = [...state.document.objects];
          const currentIndex = objects.findIndex((obj) => obj.id === objectId);

          if (currentIndex === -1 || currentIndex === newIndex) return state;

          const removed = objects.splice(currentIndex, 1)[0];
          if (removed) {
            objects.splice(newIndex, 0, removed);
          }

          return {
            document: { ...state.document, objects },
            isDirty: true,
          };
        });

        // Sync rawContent so code view reflects the move
        syncRawContent();
      },

      selectObject: (objectId: string | null) => {
        set({ selectedObjectId: objectId });
      },

      setFilterQuery: (query: string) => {
        set({ filterQuery: query });
      },

      getObjectsByClass: (className: string) => {
        const { document } = get();
        if (!document) return [];
        return document.objects.filter(
          (obj) => obj.className.toLowerCase() === className.toLowerCase()
        );
      },

      getObjectById: (objectId: string) => {
        const { document } = get();
        if (!document) return undefined;
        return document.objects.find((obj) => obj.id === objectId);
      },

      markClean: () => {
        set({ isDirty: false });
      },

      clear: () => {
        set({
          document: null,
          rawContent: '',
          errors: [],
          isParsed: false,
          isDirty: false,
          selectedObjectId: null,
          filterQuery: '',
        });
      },
    };
    },
    { name: 'IDFStore' }
  )
);

// Expose store to window for debugging (dev only)
if (typeof window !== 'undefined') {
  (window as unknown as { __IDF_STORE__: typeof useIDFStore }).__IDF_STORE__ = useIDFStore;
}

/**
 * Selector hooks for common queries
 */
export const useSelectedObject = () => {
  const selectedObjectId = useIDFStore((state) => state.selectedObjectId);
  const getObjectById = useIDFStore((state) => state.getObjectById);
  return selectedObjectId ? getObjectById(selectedObjectId) : undefined;
};

export const useFilteredObjects = () => {
  const document = useIDFStore((state) => state.document);
  const filterQuery = useIDFStore((state) => state.filterQuery);

  if (!document) return [];

  if (!filterQuery.trim()) return document.objects;

  const query = filterQuery.toLowerCase();
  return document.objects.filter(
    (obj) =>
      obj.className.toLowerCase().includes(query) ||
      obj.fields.some((f) => f.value.toLowerCase().includes(query))
  );
};

export const useObjectClassCounts = () => {
  const document = useIDFStore((state) => state.document);

  if (!document) return new Map<string, number>();

  const counts = new Map<string, number>();
  for (const obj of document.objects) {
    counts.set(obj.className, (counts.get(obj.className) || 0) + 1);
  }

  return counts;
};
